class API {

	constructor(version, resources, mode, endPointURL) {

		if (mode !== 'live' && mode !== 'test') {
			return false;
		}

		const api = this;

		// Check the hostname to determine if we're on staging
		const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
		
		// Check if we're on the staging domain
		if (hostname === 'app.staging.knapp.knapphus-kjokkensenter.no') {
			endPointURL = 'https://api.staging.knapp.knapphus-kjokkensenter.no/_api/';
		}

		// Determine if we're on staging environment by checking the URL
		if (!endPointURL) {
			throw new Error('API endpoint URL is required');
		}

		const endPoint = endPointURL + version + '/' + (mode === 'test' ? 'test/' : '');
		const requestHeaders = {
			Accept: 'application/json, text/plain',
			'Content-Type': 'application/json'
		};

		api.token = null;
		api.user = null;

		// Resource Functions
		const resourceFunctions = {
			users: usersFunc,
			dropbox: dropboxFunc,
			clients: clientsFunc,
			projects: projectsFunc,
			general: generalFunc,
			formElement: formElementFunc,
			forms: formsFunc,
			timetracks: timetracksFunc,
			todo: todoFunc
		};

		// Initialize resources with Proxy
		resources.forEach(
			function (item) {
				if (resourceFunctions[item]) {
					this[item] = new Proxy(Object.assign(new resource(item), new resourceFunctions[item]()), {
						get(target, name) {
							if (target[name]) {
								return target[name];
							} else {
								let index = 4;
								if (name.indexOf('delete') === 0) {
									index = 6;
								} else if (name.indexOf('get') === 0 || name.indexOf('put') === 0) {
									index = 3;
								}
								let method = name.substring(0, index).toUpperCase();
								name = name.substring(index).toLowerCase();
								return function (...args) {
									return this.makeRequest(name, method, null, args[0]);
								};
							}
						}
					});
				}
			}.bind(this)
		);

		// Resource base class
		function resource(name) {
			this.resourceType = name;
		}

		// Resource prototype methods
		resource.prototype.makeRequest = async function(request, method = 'GET', customHeaders, body) {

			// Check if token is set, if not, try to get it from local storage
			if (!api.token && request !== 'login') {
				let storagedUser = api.users.getStorageUser();
				if (storagedUser?.token) {
					api.token = storagedUser.token;
					api.user = storagedUser.user;
				}
			}
		
			let headers = { ...requestHeaders };

			if (api.token) {
				headers['Authorization'] = `Bearer ${api.token}`;
			}

			if (customHeaders) {
				headers = { ...headers, ...customHeaders };
			}
		
			// Fix for request handling - ensure it's a string and handle differently
			let endpoint = `${endPoint}${this.resourceType}`;
			
			// Properly handle the request parameter
			if (request !== undefined && request !== null) {
				// Convert request to string to ensure consistent handling
				const requestStr = String(request);
				
				// If it contains a question mark, it's a query string
				if (requestStr.includes('?')) {
					endpoint = `${endpoint}/${requestStr}`;
				} else {
					endpoint = `${endpoint}/${requestStr}`;
				}
			} else {
				// No request parameter provided, use the base endpoint
			}
		
			//console.log('Making API request to:', endpoint, 'Method:', method);
			
			try {
				//console.log('Sending request body:', body ? JSON.stringify(body) : 'No body');
				
				// Create an AbortController for timeout
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
				
				const response = await fetch(
					endpoint,
					{
						method,
						headers,
						body: body ? JSON.stringify(body) : undefined,
						signal: controller.signal
					}
				);
				
				// Clear timeout since request completed
				clearTimeout(timeoutId);
			
				// Handle 403 Forbidden response - redirect to login page
				if (response.status === 403) {

					console.error('Access forbidden (403) - Redirecting to login page');
					
					// Clear user data
					api.token = null;
					api.user = null;
					localStorage.removeItem('api_user');
					
					window.location.href = '/login'; // Redirect to login page
					return;

				}

				// Check if response is valid before parsing JSON
				if (!response.ok) {
					throw new Error(`HTTP error! Status: ${response.status}`);
				}
			
				const data = await response.json();
				
				if ([200, 201, 204].includes(response.status)) {
					return data;
				}
				
				throw new APIException({
					id: data.id || 0,
					def: data.def,
					message: data.message || response.statusText,
					http_code: response.status,
					body: data.errorBody || []
				});
			} catch (error) {
				if (error.name === 'AbortError') {
					console.error('API Request timed out after 15 seconds');
					throw new Error('Request timed out. Please try again later.');
				}
				console.error('API Request failed:', error);
				throw error;
			}
		};

		// CRUD shortcuts
		resource.prototype.get = function (name, params) {
			const path = params ? `${name}?${params}` : name;
			return this.makeRequest(path || this.resourceType, 'GET');
		};
		resource.prototype.post = function (body) {
			return this.makeRequest('', 'POST', null, body);
		};
		resource.prototype.put = function(id, body) {
			return this.makeRequest(id, 'PUT', null, body);
		};
		resource.prototype.delete = function (name, body) {
			if (name === '') {
				return this.makeRequest(this.resourceType, 'DELETE', null, body);
			} else {
				return this.makeRequest(name, 'DELETE', null, body);
			}
		};

		// Resource specific functions
		function usersFunc() {
			this.login = function (username, password) {
				return new Promise((resolve, reject) => {
					let customHeaders = {
						Authorization: 'Basic ' + btoa(username + ':' + password)
					};
					this.makeRequest('login', 'GET', customHeaders)
						.then((response) => {
							if (response.token) {
								api.token = response.token;
								api.user = response.user;
								localStorage.setObject('api_user', response);
								resolve(response);
							}
							throw new APIException(response);
						})
						.catch(reject);
				});
			};

			this.logout = function () {
				return new Promise((resolve, reject) => {
					this.get('logout')
						.then((response) => {
							api.token = null;
							api.user = null;
							localStorage.removeItem('api_user');
							resolve(response);
						})
						.catch(reject);
				});
			};

			this.getStorageUser = () => localStorage.getObject('api_user');
		}

		function dropboxFunc() {}
		function clientsFunc() {}
		function projectsFunc() {}
		function generalFunc() {}
		function formElementFunc() {}
		function formsFunc() {
			this.getForm = function (...args) {
				return this.get(`${args[0]}/formobject?formobject=${args[1]}`);
			};

			this.putForm = function (...args) {
				return this.put(`${args[0]}/formobject?formobject=${args[2]}`, { formObject: args[1] });
			};

			this.getUniqueID = function () {
				return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
			};
		}
		function timetracksFunc() {}
		function todoFunc() {}
	}
}

// Storage helpers
if (typeof window !== 'undefined') {
	Storage.prototype.setObject = function (key, value) {
		this.setItem(key, JSON.stringify(value));
	};

	Storage.prototype.getObject = function (key) {
		const value = this.getItem(key);
		return value && JSON.parse(value);
	};
}

// API Exception
class APIException {
	constructor(message) {
		this.id = message.id || 0;
		this.def = message.def || '';
		this.message = message.message || '';
		this.http_code = message.http_code || 0;
		this.body = message.body || '';
		this.name = 'APIException';
	}
}

// Create and export configured instance
const api = new API(
	'v1',
	['users', 'dropbox', 'clients', 'projects', 'general', 'formElement', 'forms', 'timetracks', 'todo'],
	'live',
	process.env.REACT_APP_API_ENDPOINT
);

export default api;
